/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */
import { useCallback, useRef, useState } from "react";

import { isFunction, isNil } from "@adapptio/lib-shared";

import { useEventListener } from "./useEventListener";
import { getMouseOrTouchEventData } from "@adapptio/lib-components";

export interface IUseDragHandlerData {
	movedBy: { x: number; y: number };
}

export interface IUseDragHandlers<Element extends HTMLElement = HTMLDivElement> {
	onDragStart?: (event: React.MouseEvent<Element>) => void;
	onDragMove?: (event: TouchEvent | MouseEvent, data: IUseDragHandlerData) => void;
	onDragEnd?: (event: TouchEvent | MouseEvent, data: IUseDragHandlerData) => void;
}

export interface IUseDragOpts {
	preventTextSelection?: boolean;
}

/**
 * Sets up global event listeners and styles required for dragging
 *
 */
export function useDrag<Element extends HTMLElement = HTMLElement>(
	{ onDragStart, onDragMove, onDragEnd }: IUseDragHandlers<Element> = {},
	{ preventTextSelection }: IUseDragOpts = {}
): { startDragging: React.MouseEventHandler<Element>; dragging } {
	const [ styleRuleIndex, setStyleRuleIndex ] = useState<number>(null);
	const [ dragging, setDragging ] = useState<boolean>(false);
	const dragStartData = useRef({ clientX: 0, clientY: 0 });

	const handleDragStart = useCallback<React.MouseEventHandler<Element>>(
		(event) => {
			setDragging(true);

			if (preventTextSelection) {
				const ruleIndex = document.styleSheets[0].insertRule("* { user-select:none !important }", 0);
				setStyleRuleIndex(ruleIndex);
			}

			dragStartData.current = {
				clientX: event.clientX,
				clientY: event.clientY
			};

			if (isFunction(onDragStart)) {
				onDragStart(event);
			}
		},
		[ onDragStart, preventTextSelection, setDragging ]
	);

	const handleDragMove = useCallback<(event: TouchEvent | MouseEvent) => void>(
		(event) => {
			if (dragging) {
				if (isFunction(onDragMove)) {
					const { clientX, clientY } = getMouseOrTouchEventData(event);
					const { clientX: startClientX, clientY: startClientY } = dragStartData.current;

					const data: IUseDragHandlerData = {
						movedBy: {
							x: clientX - startClientX,
							y: clientY - startClientY
						}
					};

					onDragMove(event, data);
				}
			}
		},
		[ onDragMove, dragging ]
	);

	const handleDragEnd = useCallback<(event: TouchEvent | MouseEvent) => void>(
		(event) => {
			if (!isNil(styleRuleIndex)) {
				document.styleSheets[0].deleteRule(styleRuleIndex);
				setStyleRuleIndex(null);
			}

			if (dragging) {
				setDragging(false);

				if (isFunction(onDragEnd)) {
					const { clientX, clientY } = getMouseOrTouchEventData(event);
					const { clientX: startClientX, clientY: startClientY } = dragStartData.current;

					const data: IUseDragHandlerData = {
						movedBy: {
							x: startClientX - clientX,
							y: startClientY - clientY
						}
					};

					onDragEnd(event, data);
				}
			}
		},
		[ onDragEnd, styleRuleIndex, dragging ]
	);

	useEventListener(
		document,
		"mousemove",
		(event) => {
			handleDragMove(event);
		},
		{ passive: false }
	);

	useEventListener(
		document,
		"touchmove",
		(event) => {
			handleDragMove(event);
		},
		{ passive: false }
	);

	useEventListener(document, "touchend", (event) => {
		handleDragEnd(event);
	});

	useEventListener(document, "mouseup", (event) => {
		handleDragEnd(event);
	});

	return { startDragging: handleDragStart, dragging };
}
